import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  FaEdit,
  FaTools,
  FaInfoCircle,
  FaTrash,
  FaPlus,
  FaArrowLeft,
  FaFileDownload,
  FaTable,
  FaChartPie,
  FaTachometerAlt,
  FaSearch,
  FaFilter,
  FaSyncAlt,
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import moment from 'moment-timezone';
import './Violation.css';
import './DriverViolations.css';

const DriverViolationsTracker = () =>{
  const [violations, setViolations] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('OPEN');
  // column filters removed per user request
  const [searchInput, setSearchInput] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');
  const [activeTab, setActiveTab] = useState('table');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [dateRangeDays, setDateRangeDays] = useState(30);
  const gridRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setGlobalFilter(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchSummary = React.useCallback(async () => {
    setSummaryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/driver-violations/summary?days=${dateRangeDays}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummary(resp.data || null);
    } catch (err) {
      console.error('Error fetching violations summary:', err);
    }
    finally {
      setSummaryLoading(false);
    }
  }, [dateRangeDays]);

  const fetchViolations = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/driver-violations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const normalize = (rec) => {
        const out = {};
        Object.keys(rec || {}).forEach((k) => {
          out[k.toLowerCase()] = rec[k];
        });
        out.violation_status = String(out.violation_status || '').trim().toUpperCase()
          || (String(out.resolved || '').trim().toUpperCase() === 'Y' ? 'RESOLVED' : 'OPEN');
        return out;
      };

      setViolations((response.data || []).map(normalize));
    } catch (err) {
      console.error('Error fetching violations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchViolations();
  }, [fetchViolations]);

  useEffect(() => {
    if (activeTab === 'dashboard') fetchSummary();
  }, [activeTab, fetchSummary]);

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const filteredViolations = useMemo(() => {
    return violations
      .filter((v) => (selectedStatus ? v.violation_status === selectedStatus : true))
      .filter((v) =>
        globalFilter
          ? Object.values(v)
              .join(' ')
              .toLowerCase()
              .includes(globalFilter.toLowerCase())
          : true
      );
  }, [violations, selectedStatus, globalFilter]);

  const chartData = useMemo(() => {
    if (!filteredViolations.length) return { pieData: [], barData: [] };

    const issueCount = filteredViolations.reduce((acc, record) => {
      const issue = record.violation || 'Unknown';
      acc[issue] = (acc[issue] || 0) + 1;
      return acc;
    }, {});

    const pieData = Object.keys(issueCount).map((issue) => ({
      name: issue,
      value: issueCount[issue],
      percentage: ((issueCount[issue] / filteredViolations.length) * 100).toFixed(1),
    }));

    const carrierCount = filteredViolations.reduce((acc, record) => {
      const carrier = record.carrier || 'Unknown';
      acc[carrier] = (acc[carrier] || 0) + 1;
      return acc;
    }, {});

    const barData = Object.keys(carrierCount).map((carrier) => ({
      name: carrier,
      count: carrierCount[carrier],
    }));

    return { pieData, barData };
  }, [filteredViolations]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return moment(date).format('DD/MM/YYYY HH:mm');
  };

  const handleDeleteViolation = React.useCallback(async (id) => {
    if (!id) return;
    if (!window.confirm('Delete this violation? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_BASE_API_URL}/driver-violations/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      // refresh list
      fetchViolations();
      window.alert('Violation deleted');
    } catch (err) {
      console.error('Error deleting violation:', err);
      window.alert('Failed to delete violation');
    }
  }, [fetchViolations]);

  const dateFilterParams = useMemo(
    () => ({
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
        const cellDate = new Date(cellValue);
        if (Number.isNaN(cellDate.getTime())) return -1;

        const filterDate = new Date(filterLocalDateAtMidnight);
        const cellOnlyDate = new Date(
          cellDate.getFullYear(),
          cellDate.getMonth(),
          cellDate.getDate()
        );

        if (cellOnlyDate < filterDate) return -1;
        if (cellOnlyDate > filterDate) return 1;
        return 0;
      },
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      { headerName: 'JP Number', field: 'journey_plane_no', minWidth: 130 },
      { headerName: 'Truck Number', field: 'scheduled_vehicle', minWidth: 140 },
      {
        headerName: 'Violation Time',
        field: 'violation_time',
        minWidth: 170,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        valueFormatter: ({ value }) => formatDateTime(value),
        sort: 'desc',
      },
      {
        headerName: 'Violation Type',
        field: 'violation',
        minWidth: 170,
        valueGetter: (params) => params.data?.violation || params.data?.violation_type || '—',
      },
      { headerName: 'Driver Name', field: 'driver_name', minWidth: 160 },
      {
        headerName: 'Driver Phone',
        field: 'driver_phone',
        minWidth: 140,
        valueFormatter: ({ value }) => value || 'N/A',
      },
      { headerName: 'Carrier', field: 'carrier', minWidth: 120 },
      {
        headerName: 'IVMS Point',
        field: 'ivms_point',
        minWidth: 140,
        valueFormatter: ({ value }) => value || 'N/A',
      },
      {
        headerName: 'Remarks',
        field: 'remarks',
        minWidth: 220,
        flex: 1.4,
        cellClass: 'bt-remarks-cell',
        valueFormatter: ({ value }) => value || 'N/A',
      },
      {
        headerName: 'Status',
        field: 'violation_status',
        minWidth: 130,
        cellRenderer: ({ value }) => (
          <div className="bt-status-badge">{value || 'OPEN'}</div>
        ),
      },
      {
        headerName: 'Date Resolved',
        field: 'date_resolved',
        minWidth: 170,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        valueFormatter: ({ value }) => formatDateTime(value),
      },
      {
        headerName: 'Actions',
        field: 'actions',
        minWidth: 150,
        filter: false,
        sortable: false,
        pinned: 'right',
        cellRenderer: ({ data: violation }) => (
          <div className="bt-action-icons">
            <button className="bt-action-btn bt-edit-btn" title="Edit" onClick={() => navigate(`/edit-violation/${violation.violation_id}`)}>
              <FaEdit />
            </button>
            <button className="bt-action-btn bt-handle-btn" title="Handle" onClick={() => navigate(`/handle-violation/${violation.violation_id}`)}>
              <FaTools />
            </button>
            <button className="bt-action-btn bt-info-btn" title="Info" onClick={() => navigate(`/info-violation/${violation.violation_id}`)}>
              <FaInfoCircle />
            </button>
            <button className="bt-action-btn bt-delete-btn" title="Delete" onClick={() => handleDeleteViolation(violation.violation_id)}>
              <FaTrash />
            </button>
          </div>
        ),
      },
    ],
    [dateFilterParams, handleDeleteViolation, navigate]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: false,
    }),
    []
  );

  const resetTableFilters = () => {
    setSearchInput('');
    setGlobalFilter('');

    const api = gridRef.current?.api;
    if (!api) return;

    api.setFilterModel(null);
    api.applyColumnState({
      defaultState: { sort: null },
    });
  };

  const getExportRows = () => {
    const visibleRows = [];
    const api = gridRef.current?.api;

    if (!api) return filteredViolations;

    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) visibleRows.push(node.data);
    });

    return visibleRows;
  };

  const handleExport = () => {
    const rowsToExport = getExportRows();

    if (!rowsToExport.length) {
      window.alert('No violation records match the current filters.');
      return;
    }

    if (window.confirm(`Are you sure you want to export ${rowsToExport.length} records to Excel?`)) {
      const exportData = rowsToExport.map((v) => ({
        'JP Number': v.journey_plane_no,
        'Truck Number': v.scheduled_vehicle,
        'Violation Time': formatDateTime(v.violation_time),
        'Violation Type': v.violation || v.violation_type || '—',
        'Driver Name': v.driver_name,
        'Driver Phone': v.driver_phone || 'N/A',
        Carrier: v.carrier,
        'IVMS Point': v.ivms_point || 'N/A',
        Remarks: v.remarks || 'N/A',
        Status: v.violation_status || 'OPEN',
        'Date Resolved': formatDateTime(v.date_resolved),
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      if (exportData.length) {
        const headers = Object.keys(exportData[0]);
        ws['!autofilter'] = {
          ref: XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: exportData.length, c: headers.length - 1 },
          }),
        };
        ws['!cols'] = headers.map((header) => ({
          wch: Math.min(
            45,
            Math.max(
              header.length,
              ...exportData.map((row) =>
                row[header] ? String(row[header]).length : 0
              )
            ) + 2
          ),
        }));
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Violations');
      const date = new Date().toISOString().split('T')[0];
      const fileName = `Violations_${date}.xlsx`;
      XLSX.writeFile(wb, fileName);
    }
  };

  const PIE_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
  const BAR_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const hasData = filteredViolations.length > 0;

  return (
    <div className="driver-violations-root bt-container">
      <div className="bt-header">
        <div className="bt-header-main">
          <div className="bt-header-left">
            <button className="bt-back-btn" onClick={() => navigate('/main')} title="Back to Main Page"><FaArrowLeft /></button>
            <div className="bt-header-title">
              <h1>Driver Violations Tracker</h1>
              <p>Manage and analyze driver violation records</p>
            </div>
          </div>
          <div className="bt-header-actions">
            <div className="bt-filter-group">
              <FaFilter className="bt-filter-icon" />
              <select value={selectedStatus} onChange={(e) => handleStatusChange(e.target.value)} className="bt-status-filter">
                <option value="">All Status</option>
                <option value="OPEN">OPEN</option>
                <option value="RESOLVED">RESOLVED</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="bt-main-content">
        <div className="bt-action-bar">
          <div className="bt-action-left">
            <button onClick={() => navigate('/add-violation')} className="bt-primary-btn"><FaPlus />Add New Violation</button>
          </div>
          <div className="bt-action-right">
            <div className="bt-search-box">
              <FaSearch className="bt-search-icon" />
              <input
                type="text"
                placeholder="Search records..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="bt-search-input"
              />
            </div>
            <button
              className="bt-icon-btn"
              onClick={resetTableFilters}
              title="Reset table filters"
              aria-label="Reset table filters"
              type="button"
            >
              <FaSyncAlt />
            </button>
            <button className="bt-export-btn" onClick={handleExport} disabled={!hasData}><FaFileDownload />Export Excel</button>
          </div>
        </div>

        <div className="bt-tab-nav">
          <button className={`bt-tab-btn ${activeTab === 'table' ? 'bt-tab-active' : ''}`} onClick={() => setActiveTab('table')}>
            <FaTable /> Violations List <span className="bt-tab-count">{filteredViolations.length}</span>
          </button>
          <button className={`bt-tab-btn ${activeTab === 'dashboard' ? 'bt-tab-active' : ''}`} onClick={() => setActiveTab('dashboard')} disabled={!hasData}>
            <FaChartPie /> Analytics Dashboard
          </button>
        </div>

        {selectedStatus === 'OPEN' && (
          <div style={{ marginTop: 8, color: '#374151', fontSize: 13 }}>
            Showing <strong>OPEN</strong> violations by default. Use the status filter to change.
          </div>
        )}

        {activeTab === 'table' && (
          <div className="bt-table-section">
            <div className="bt-table-container">
              {hasData ? (
                <div className="ag-theme-alpine bt-ag-grid">
                  <AgGridReact
                    ref={gridRef}
                    rowData={filteredViolations}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowHeight={28}
                    headerHeight={36}
                    pagination={true}
                    paginationPageSize={40}
                    animateRows={true}
                  />
                </div>
              ) : (
                <div className="bt-empty-state">
                  <FaTachometerAlt className="bt-empty-icon" />
                  <h3>No violation records found</h3>
                  <p>Try adjusting your search or filter criteria, or add new violation records</p>
                  <button onClick={() => navigate('/add-violation')} className="bt-primary-btn"><FaPlus />Add First Violation</button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'dashboard' && (
          <div className="bt-dashboard-section">
            {summaryLoading ? (
              <div className="bt-dashboard-loader">Loading analytics…</div>
            ) : hasData ? (
              <>
                <div className="bt-stats-overview">
                  <div className="bt-stat-card">
                    <div className="bt-stat-icon total"><FaTachometerAlt /></div>
                    <div className="bt-stat-info"><h3>Total Violations</h3><span className="bt-stat-value">{summary ? summary.total : filteredViolations.length}</span></div>
                  </div>

                  <div className="bt-stat-card">
                    <div className="bt-stat-icon in-transit"><FaTools /></div>
                    <div className="bt-stat-info"><h3>Open</h3><span className="bt-stat-value">{summary ? summary.open : filteredViolations.filter(v => v.violation_status === 'OPEN' || !v.violation_status).length}</span></div>
                  </div>

                  <div className="bt-stat-card">
                    <div className="bt-stat-icon resolved"><FaInfoCircle /></div>
                    <div className="bt-stat-info"><h3>Resolved</h3><span className="bt-stat-value">{summary ? summary.resolved : filteredViolations.filter(v => v.violation_status === 'RESOLVED').length}</span></div>
                  </div>

                  <div className="bt-stat-card">
                    <div className="bt-stat-icon pending"><FaChartPie /></div>
                    <div className="bt-stat-info"><h3>Repeat Offenders</h3><span className="bt-stat-value">{summary ? summary.repeatOffenders : 0}</span></div>
                  </div>
                </div>

                <div className="bt-charts-grid">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gridColumn: '1 / -1', marginBottom: 8 }}>
                    <div style={{ fontWeight: 'bold', fontSize: 13 }}>Trend:</div>
                    <div>
                      <select className="bt-date-select" value={dateRangeDays} onChange={(e) => setDateRangeDays(Number(e.target.value))}>
                        <option value={7}>7d</option>
                        <option value={30}>30d</option>
                        <option value={90}>90d</option>
                      </select>
                    </div>
                  </div>
                  <div className="bt-chart-card" style={{ gridColumn: '1 / -1' }}>
                    <div className="bt-chart-header"><h3>Violations Trend (last {dateRangeDays} days)</h3></div>
                    <div className="bt-chart-container">
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={summary && summary.timeseries ? summary.timeseries : []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tickFormatter={(t) => (t ? moment(t).format('DD/MM') : t)} />
                          <YAxis />
                          <Tooltip />
                          <Line type="monotone" dataKey="count" stroke="#4f46e5" strokeWidth={2} dot={{ r: 2 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="bt-chart-card bt-pie-card">
                    <div className="bt-chart-header"><h3>Violation Type Distribution</h3></div>
                    <div className="bt-chart-container bt-pie-container">
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie 
                            data={chartData.pieData} 
                            cx="50%" 
                            cy="45%" 
                            labelLine={false} 
                            label={({ name, percentage }) => `${name}: ${percentage}%`} 
                            outerRadius={75} 
                            innerRadius={40} 
                            paddingAngle={1} 
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                          >
                            {chartData.pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]} />
                          <Legend verticalAlign="bottom" height={36} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bt-chart-card bt-bar-card">
                    <div className="bt-chart-header"><h3>Violations by Driver</h3></div>
                    <div className="bt-chart-container bt-bar-container">
                      <ResponsiveContainer width="100%" height={300}>
                        {/* prefer backend summary.topOffenders when available */}
                        <BarChart
                          data={summary && summary.topOffenders ? summary.topOffenders.map(d => ({ name: d.driver_name || d.driver, count: d.count })) : chartData.barData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(value) => [value, 'Count']} cursor={{ fill: 'rgba(79, 70, 229, 0.1)' }} />
                          <Bar dataKey="count" name="Violations" fill={BAR_COLORS[0]} radius={[0, 6, 6, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bt-empty-dashboard">
                <FaChartPie className="bt-empty-icon" />
                <h3>No data available for analysis</h3>
                <p>Add violation records to view analytics dashboard</p>
                <button onClick={() => navigate('/add-violation')} className="bt-primary-btn"><FaPlus />Add Violation Record</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverViolationsTracker;
